import React from 'react';

import type {
  HandlerDetectPolicy,
  HandlerFaceQualityChecks,
  HandlerRecord,
} from '@vlabs/api/artemy/model';
import {
  FACE_OCCLUSION_STATE_OPTIONS_STRICT,
} from '@vlabs/api-bindings/src/constants';
import {
  FACE_QUALITY_EYE,
  FACE_QUALITY_EYEBROWS_STATE,
  FACE_QUALITY_GLASSES,
  FACE_QUALITY_HEADWEAR_TYPE,
  FACE_QUALITY_IMAGE_FORMAT,
  FACE_QUALITY_SHOULDERS_POSITION,
  FACE_QUALITY_SMILE_PROPERTIES,
  FACE_QUALITY_YES_NO,
} from '@vlabs/api-bindings/src/luna-client/handlers/constants';
import { Control, Tag } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import {
  ViewCard, ViewRow, ViewRowCheckbox, ViewRowTitle,
} from '../components';
import { useHandlerViewPageTranslation } from '../i18n';
import { formatRange, isEnabled } from '../viewModel';
import { optionLabel, optionLabels } from './options';

interface DetectionSectionProps {
  handler: HandlerRecord;
}

interface AttributeConfig {
  source: 'detect' | 'extract';
  field: string;
  labelKey: string;
}

const FACE_ATTRIBUTES: AttributeConfig[] = [
  { source: 'extract', field: 'extract_face_descriptor', labelKey: 'handlers:policies.extract_policy.extract_face_descriptor' },
  { source: 'extract', field: 'extract_basic_attributes', labelKey: 'handlers:policies.extract_policy.extract_basic_attributes' },
  { source: 'detect', field: 'estimate_head_pose', labelKey: 'handlers:policies.detect_policy.estimate_head_pose.label' },
  { source: 'detect', field: 'estimate_emotions', labelKey: 'handlers:policies.detect_policy.estimate_emotions' },
  { source: 'detect', field: 'estimate_mask', labelKey: 'handlers:policies.detect_policy.estimate_mask' },
  { source: 'detect', field: 'estimate_quality', labelKey: 'handlers:policies.detect_policy.estimate_quality' },
  { source: 'detect', field: 'estimate_gaze', labelKey: 'handlers:policies.detect_policy.estimate_gaze.label' },
  { source: 'detect', field: 'estimate_glasses', labelKey: 'handlers:policies.detect_policy.estimate_glasses' },
  { source: 'detect', field: 'estimate_eyes_attributes', labelKey: 'handlers:policies.detect_policy.estimate_eyes_attributes' },
  { source: 'detect', field: 'estimate_mouth_attributes', labelKey: 'handlers:policies.detect_policy.estimate_mouth_attributes' },
  { source: 'detect', field: 'detect_landmarks68', labelKey: 'handlers:policies.detect_policy.detect_landmarks68.label' },
  { source: 'detect', field: 'extract_exif', labelKey: 'handlers:policies.detect_policy.extract_exif' },
];

const BODY_ATTRIBUTES: AttributeConfig[] = [
  { source: 'extract', field: 'extract_body_descriptor', labelKey: 'handlers:policies.extract_policy.extract_body_descriptor' },
  { source: 'detect', field: 'estimate_basic_attributes', labelKey: 'handlers:policies.detect_policy.body_attributes.estimate_basic_attributes' },
  { source: 'detect', field: 'estimate_upper_body', labelKey: 'handlers:policies.detect_policy.body_attributes.estimate_upper_body' },
  { source: 'detect', field: 'estimate_lower_body', labelKey: 'handlers:policies.detect_policy.body_attributes.estimate_lower_body' },
  { source: 'detect', field: 'estimate_accessories', labelKey: 'handlers:policies.detect_policy.body_attributes.estimate_accessories' },
];

const QUALITY_MIN_MAX_CHECKS: { field: keyof HandlerFaceQualityChecks; labelKey: string }[] = [
  { field: 'image_size', labelKey: 'imageSize' },
  { field: 'image_width', labelKey: 'imageWidth' },
  { field: 'image_height', labelKey: 'imageHeight' },
  { field: 'aspect_ratio', labelKey: 'aspectRatio' },
  { field: 'illumination_quality', labelKey: 'illuminationQuality' },
  { field: 'specularity_quality', labelKey: 'specularity_quality' },
  { field: 'blurriness_quality', labelKey: 'blurrinessQuality' },
  { field: 'dark_quality', labelKey: 'darkQuality' },
  { field: 'light_quality', labelKey: 'lightQuality' },
  { field: 'illumination_uniformity', labelKey: 'illuminationUniformity' },
  { field: 'dynamic_range', labelKey: 'dynamicRange' },
  { field: 'background_uniformity', labelKey: 'backgroundUniformity' },
  { field: 'background_lightness', labelKey: 'backgroundLightness' },
  { field: 'face_width', labelKey: 'faceWidth' },
  { field: 'face_height', labelKey: 'faceHeight' },
  { field: 'indent_upper', labelKey: 'indentUpper' },
  { field: 'indent_lower', labelKey: 'indentLower' },
  { field: 'indent_left', labelKey: 'indentLeft' },
  { field: 'indent_right', labelKey: 'indentRight' },
  { field: 'head_yaw', labelKey: 'headYaw' },
  { field: 'head_pitch', labelKey: 'headPitch' },
  { field: 'head_roll', labelKey: 'headRoll' },
  { field: 'gaze_yaw', labelKey: 'gazeYaw' },
  { field: 'gaze_pitch', labelKey: 'gazePitch' },
  { field: 'mouth_smiling', labelKey: 'mouthSmiling' },
  { field: 'mouth_occluded', labelKey: 'mouthOccluded' },
  { field: 'mouth_open', labelKey: 'mouthOpen' },
  { field: 'eye_distance', labelKey: 'eyeDistance' },
  { field: 'head_width', labelKey: 'headWidth' },
  { field: 'head_height', labelKey: 'headHeight' },
  { field: 'head_horizontal_center', labelKey: 'headHorizontalCenter' },
  { field: 'head_vertical_center', labelKey: 'headVerticalCenter' },
];

const QUALITY_STRING_ARRAY_CHECKS: {
  field: keyof HandlerFaceQualityChecks;
  labelKey: string;
  options: unknown;
}[] = [
  { field: 'image_format', labelKey: 'imageFormat', options: FACE_QUALITY_IMAGE_FORMAT },
  { field: 'glasses', labelKey: 'glasses', options: FACE_QUALITY_GLASSES },
  { field: 'left_eye', labelKey: 'leftEye', options: FACE_QUALITY_EYE },
  { field: 'right_eye', labelKey: 'rightEye', options: FACE_QUALITY_EYE },
  { field: 'eyebrows_state', labelKey: 'eyebrowsState', options: FACE_QUALITY_EYEBROWS_STATE },
  { field: 'headwear_type', labelKey: 'headwearType', options: FACE_QUALITY_HEADWEAR_TYPE },
  { field: 'smile_properties', labelKey: 'smileProperties', options: FACE_QUALITY_SMILE_PROPERTIES },
  { field: 'shoulders_position', labelKey: 'shoulders_position', options: FACE_QUALITY_SHOULDERS_POSITION },
];

const QUALITY_NUMBER_CHECKS: { field: keyof HandlerFaceQualityChecks; labelKey: string }[] = [
  { field: 'radial_distortion', labelKey: 'radialDistortion' },
  { field: 'red_eyes', labelKey: 'redEyes' },
  { field: 'natural_light', labelKey: 'naturalLight' },
];

interface DetectCardProps {
  value: string;
  enabled: boolean;
  title: string;
  attributesLabel: string;
  attributeChips: string[];
}

function DetectCard({
  value,
  enabled,
  title,
  attributesLabel,
  attributeChips,
}: DetectCardProps): React.JSX.Element | null {
  if (!enabled) return null;

  return (
    <ViewCard
      header={(
        <ViewRowTitle label={title}>
          <Control.Checkbox
            checked
            disabled
          />
        </ViewRowTitle>
      )}
      value={value}
    >
      <ViewRow label={attributesLabel}>
        {attributeChips.map((label) => (
          <Tag
            key={label}
            kind="neutral"
            shape="square"
            value={label}
          />
        ))}
      </ViewRow>
    </ViewCard>
  );
}

function DetectionSection({ handler }: DetectionSectionProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { t: tView } = useHandlerViewPageTranslation();
  const detectPolicy: HandlerDetectPolicy | undefined = handler.policies.detect_policy;
  if (!detectPolicy) return null;

  const detectRecord = detectPolicy as unknown as Record<string, unknown>;
  const extractRecord = handler.policies.extract_policy as unknown as Record<string, unknown>;
  const bodyAttributesRecord = (detectPolicy.body_attributes ?? {}) as unknown as Record<string, unknown>;

  const attributeChips = (configs: AttributeConfig[]): string[] => configs
    .filter(({ source, field }) => (
      source === 'extract' ? isEnabled(extractRecord[field]) : isEnabled(detectRecord[field])
    ))
    .map(({ labelKey }) => t(labelKey));

  const faceAttributeChips = attributeChips(FACE_ATTRIBUTES);
  const bodyAttributeChips = BODY_ATTRIBUTES
    .filter(({ source, field }) => (
      source === 'extract' ? isEnabled(extractRecord[field]) : isEnabled(bodyAttributesRecord[field])
    ))
    .map(({ labelKey }) => t(labelKey));

  const faceQuality = detectPolicy.face_quality;
  const checks = faceQuality?.checks;

  const qualityLabel = (labelKey: string): string => (
    labelKey === 'specularity_quality'
      ? tView('quality.specularity_quality')
      : t(`handlers:качество лица.${labelKey}`)
  );

  const qualityMinMaxRows = QUALITY_MIN_MAX_CHECKS
    .map(({ field, labelKey }) => {
      const check = checks?.[field];
      if (!check || !isEnabled(check.estimate)) return null;

      const threshold = check.threshold as { min?: number; max?: number } | undefined;
      const value = formatRange(threshold?.min, threshold?.max);
      if (!value) return null;

      return (
        <ViewRow
          key={field}
          label={qualityLabel(labelKey)}
        >
          {value}
        </ViewRow>
      );
    });

  const qualityChipRows = QUALITY_STRING_ARRAY_CHECKS
    .map(({ field, labelKey, options }) => {
      const check = checks?.[field];
      if (!check || !isEnabled(check.estimate)) return null;

      const labels = optionLabels(options, check.threshold);
      if (labels.length === 0) return null;

      return (
        <ViewRow
          key={field}
          label={qualityLabel(labelKey)}
        >
          {labels.map((label) => (
            <Tag
              key={label}
              kind="neutral"
              shape="square"
              value={label}
            />
          ))}
        </ViewRow>
      );
    });

  const qualityNumberRows = QUALITY_NUMBER_CHECKS
    .map(({ field, labelKey }) => {
      const check = checks?.[field];
      if (!check || !isEnabled(check.estimate) || check.threshold === undefined) return null;

      return (
        <ViewRow
          key={field}
          label={qualityLabel(labelKey)}
        >
          {optionLabel(FACE_QUALITY_YES_NO, check.threshold) ?? String(check.threshold)}
        </ViewRow>
      );
    });

  const imageModification = checks?.image_modification;
  const imageModificationRow = (imageModification && isEnabled(imageModification.estimate))
    ? (
      <ViewRow label={tView('quality.image_modification')}>
        {optionLabel(FACE_QUALITY_YES_NO, imageModification.threshold)
          ?? (imageModification.threshold !== undefined ? String(imageModification.threshold) : '')}
      </ViewRow>
    )
    : null;

  const qualityRows = [
    ...qualityChipRows,
    imageModificationRow,
    ...qualityMinMaxRows,
    ...qualityNumberRows,
  ];

  const qualityCard = faceQuality && (
    isEnabled(faceQuality.estimate) || isEnabled(faceQuality.filter) || qualityRows.some(Boolean)
  )
    ? (
      <ViewCard
        defaultOpen={isEnabled(faceQuality.estimate)}
        header={(
          <ViewRowTitle label={t('handlers:setup sections.face quality.title')}>
            <Control.Checkbox
              checked={isEnabled(faceQuality.estimate)}
              disabled
            />
          </ViewRowTitle>
        )}
        value="face-quality"
      >
        <ViewRowCheckbox
          checked={isEnabled(faceQuality.filter)}
          label={tView('quality.filter')}
        />
        {qualityRows}
      </ViewCard>
    )
    : null;

  const occlusion = detectPolicy.estimate_face_occlusion;
  const occlusionStatesLabel = (detectPolicy.face_occlusion_states ?? [])
    .map((state) => optionLabel(FACE_OCCLUSION_STATE_OPTIONS_STRICT, state))
    .filter((label): label is string => !!label)
    .join(', ');

  const occlusionThresholds = [
    { field: 'hair_occlusion_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.hair_occlusion_threshold.label' },
    { field: 'occlusion_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.occlusion_threshold.label' },
    { field: 'forehead_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.forehead_threshold.label' },
    { field: 'eye_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.eye_threshold.label' },
    { field: 'nose_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.nose_threshold.label' },
    { field: 'mouth_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.mouth_threshold.label' },
    { field: 'lower_face_threshold', labelKey: 'handlers:policies.detect_policy.estimate_face_occlusion.lower_face_threshold.label' },
  ] as const;

  const occlusionRows = occlusionThresholds
    .map(({ field, labelKey }) => {
      const value = occlusion?.[field];
      if (value === undefined || value === null) return null;

      return (
        <ViewRow
          key={field}
          label={t(labelKey)}
        >
          {String(value)}
        </ViewRow>
      );
    });

  const peopleCount = detectPolicy.estimate_people_count;

  return (
    <React.Fragment>
      <DetectCard
        attributeChips={faceAttributeChips}
        attributesLabel={tView('detection.face_attributes')}
        enabled={isEnabled(detectPolicy.detect_face) && faceAttributeChips.length > 0}
        title={tView('detection.detect_faces')}
        value="detect-faces"
      />

      {isEnabled(detectPolicy.estimate_liveness?.estimate) && (
        <ViewCard
          header={(
            <ViewRowTitle label={t('handlers:policies.detect_policy.estimate_liveness.estimate')}>
              <Control.Checkbox
                checked
                disabled
              />
            </ViewRowTitle>
          )}
          value="liveness"
        >
          <ViewRow label={t('handlers:policies.detect_policy.estimate_liveness.liveness_threshold.label')}>
            {String(detectPolicy.estimate_liveness?.liveness_threshold ?? '')}
          </ViewRow>
        </ViewCard>
      )}

      {isEnabled(detectPolicy.estimate_deepfake?.estimate) && (
        <ViewCard
          header={(
            <ViewRowTitle label={t('handlers:policies.detect_policy.estimate_deepfake.estimate')}>
              <Control.Checkbox
                checked
                disabled
              />
            </ViewRowTitle>
          )}
          value="deepfake"
        >
          <ViewRow label={t('handlers:policies.detect_policy.estimate_deepfake.real_threshold.label')}>
            {String(detectPolicy.estimate_deepfake?.real_threshold ?? '')}
          </ViewRow>
        </ViewCard>
      )}

      {qualityCard}

      {isEnabled(occlusion?.estimate) && (
        <ViewCard
          header={(
            <ViewRowTitle label={t('handlers:policies.detect_policy.estimate_face_occlusion.estimate')}>
              <Control.Checkbox
                checked
                disabled
              />
            </ViewRowTitle>
          )}
          value="face-occlusion"
        >
          {occlusionStatesLabel
            ? (
              <ViewRow label={t('handlers:policies.detect_policy.face_occlusion_states')}>
                {occlusionStatesLabel}
              </ViewRow>
            )
            : null}
          {occlusionRows}
        </ViewCard>
      )}

      <DetectCard
        attributeChips={bodyAttributeChips}
        attributesLabel={tView('detection.body_attributes')}
        enabled={isEnabled(detectPolicy.detect_body) && bodyAttributeChips.length > 0}
        title={tView('detection.detect_bodies')}
        value="detect-bodies"
      />

      {!!peopleCount && (isEnabled(peopleCount.estimate) || isEnabled(peopleCount.people_coordinates)) && (
        <ViewCard
          defaultOpen={isEnabled(peopleCount.estimate)}
          header={(
            <ViewRowTitle label={t('handlers:policies.detect_policy.estimate_people_count.estimate')}>
              <Control.Checkbox
                checked={isEnabled(peopleCount.estimate)}
                disabled
              />
            </ViewRowTitle>
          )}
          value="people-count"
        >
          <ViewRowCheckbox
            checked={isEnabled(peopleCount.people_coordinates)}
            label={t('handlers:policies.detect_policy.estimate_people_count.people_coordinates')}
          />
        </ViewCard>
      )}
    </React.Fragment>
  );
}

export { DetectionSection };
